import type { ActivityLog } from '@core/entities/ActivityLog';
import type {
  ActivityLogListParams,
  ActivityLogRepository,
} from '@core/repositories/ActivityLogRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import type { PaginatedResponse } from '@shared/types';

interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

interface ActivityLogResponse {
  id: string;
  action: string;
  description: string;
  subject_type: string | null;
  user: { id: string; name: string } | null;
  created_at: string;
}

const toActivityLog = (payload: ActivityLogResponse): ActivityLog => ({
  id: payload.id,
  action: payload.action,
  description: payload.description,
  subjectType: payload.subject_type,
  user: payload.user,
  createdAt: payload.created_at,
});

export class ActivityLogRepositoryImpl implements ActivityLogRepository {
  async list(
    workspaceId: string,
    params?: ActivityLogListParams,
  ): Promise<PaginatedResponse<ActivityLog>> {
    try {
      const response = await httpClient.get<LaravelPaginatedResponse<ActivityLogResponse>>(
        `/workspaces/${workspaceId}/activity-logs`,
        {
          params: {
            action: params?.action || undefined,
            search: params?.search || undefined,
            page: params?.page,
            per_page: params?.perPage,
          },
        },
      );

      return {
        data: response.data.data.map(toActivityLog),
        meta: {
          currentPage: response.data.meta.current_page,
          lastPage: response.data.meta.last_page,
          perPage: response.data.meta.per_page,
          total: response.data.meta.total,
        },
      };
    } catch (error) {
      throw toAppError(error);
    }
  }
}
